import { usePortfolioSpeciesSource } from 'src/app/portfolio/state/portfolioSpeciesSource';
import {
    useMapMarkersQuery,
    useSiteTableQuery,
    useSitePopupQuery,
    useNextMapMarkersQuery,
    useNextSiteTableQuery,
    useNextSitePopupQuery,
} from '../state/api/portfolioSiteExplorerGraphSlice';

/**
 * The three species-carrying explorer queries, resolved to whichever source the
 * surrounding route declared. Components call these instead of the slice directly, so
 * one set of components serves both the live and the grid-backed pages.
 *
 * Both hooks in each pair are always called — conditionally calling one would break
 * the Rules of Hooks — and the inactive one is skipped, so only one request goes out.
 * Skipping is what makes this safe rather than merely tidy: without it, every page
 * would read both sources.
 */

type QueryOptions = { skip?: boolean } | undefined;

/** Skip the query unless it is the active source, keeping any skip the caller asked for. */
const optionsFor = (isActive: boolean, options: QueryOptions) => ({
    ...options,
    skip: Boolean(options?.skip) || !isActive,
});

export const useExplorerMapMarkers = (args: Parameters<typeof useMapMarkersQuery>[0], options?: QueryOptions) => {
    const isGrid = usePortfolioSpeciesSource() === 'grid';
    const projection = useMapMarkersQuery(args, optionsFor(!isGrid, options));
    const grid = useNextMapMarkersQuery(args, optionsFor(isGrid, options));
    return isGrid ? grid : projection;
};

export const useExplorerSiteTable = (args: Parameters<typeof useSiteTableQuery>[0], options?: QueryOptions) => {
    const isGrid = usePortfolioSpeciesSource() === 'grid';
    const projection = useSiteTableQuery(args, optionsFor(!isGrid, options));
    const grid = useNextSiteTableQuery(args, optionsFor(isGrid, options));
    return isGrid ? grid : projection;
};

export const useExplorerSitePopup = (args: Parameters<typeof useSitePopupQuery>[0], options?: QueryOptions) => {
    const isGrid = usePortfolioSpeciesSource() === 'grid';
    const projection = useSitePopupQuery(args, optionsFor(!isGrid, options));
    const grid = useNextSitePopupQuery(args, optionsFor(isGrid, options));
    return isGrid ? grid : projection;
};
